"use client"

import * as React from "react"
import ReactSelect, {
  components,
  type ClearIndicatorProps,
  type DropdownIndicatorProps,
  type GroupBase,
  type GroupHeadingProps,
  type InputActionMeta,
  type MenuListProps,
  type MenuProps,
  type MultiValueGenericProps,
  type OptionProps,
  type SelectInstance,
} from "react-select"
import makeAnimated from "react-select/animated"
import CreatableSelect from "react-select/creatable"
import { ChevronDownIcon, PlusIcon, XIcon } from "lucide-react"

import { cn } from "../../lib/utils"
import { pillColorClasses } from "../../lib/pill-colors"
import { useIsMobile } from "../../lib/use-mobile"
import { MobilePickerSheet, useMobilePickerLabels, type MobilePickerOption } from "./mobile-picker"

export type SelectOption<TValue extends string = string> = {
  className?: string
  disabled?: boolean
  label: React.ReactNode
  testId?: string
  textValue?: string
  value: TValue
}

export type SelectOptionGroup<TValue extends string = string> = {
  label: React.ReactNode
  options: SelectOption<TValue>[]
}

export type SelectOptionEntry<TValue extends string = string> = SelectOption<TValue> | SelectOptionGroup<TValue>

/** A sticky "create" row shown at the bottom of a Select's menu (e.g. "Create a new category"). */
export type SelectCreateAction = {
  label: React.ReactNode
  onClick: () => void
}

type MobileSelectItemRenderer<TValue extends string = string> = (
  option: SelectOption<TValue>,
  state: { selected: boolean; disabled: boolean },
) => React.ReactNode

type SelectBaseProps<TValue extends string = string> = {
  options: SelectOptionEntry<TValue>[]
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (nextOpen: boolean) => void
  disabled?: boolean
  isSearchable?: boolean
  hideSelectedOptions?: boolean
  placeholder?: React.ReactNode
  className?: string
  controlClassName?: string
  menuClassName?: string
  isClearable?: boolean
  clearLabel?: React.ReactNode
  /**
   * Allow the user to enter a value that isn't in `options`. Desktop uses react-select's
   * CreatableSelect; the mobile sheet shows a "create" row for the typed text.
   */
  creatable?: boolean
  /** Label for the create entry (e.g. `(input) => \`Use "${input}"\``). Defaults to the raw input. */
  formatCreateLabel?: (inputValue: string) => string
  /** Show a search input for local option filtering. Creatable selects are searchable automatically. */
  searchEnabled?: boolean
  size?: "sm" | "default" | "lg"
  /** Multi-select: render each selected chip in a stable colour from the shared pill palette. */
  coloredPills?: boolean
  menuAlign?: "start" | "end"
  noOptionsMessage?: React.ReactNode | ((state: { inputValue: string }) => React.ReactNode)
  /**
   * Optional sticky action row rendered at the bottom of the menu (desktop) and the bottom sheet (mobile) —
   * e.g. "Create a new category". It is always shown (including when there are no options), closes the menu,
   * and calls `onClick`. Use it to let the user create the missing entity inline instead of leaving the form.
   */
  createAction?: SelectCreateAction
  mobileItemRenderer?: MobileSelectItemRenderer<TValue>
  mobileEmpty?: React.ReactNode
  mobileTitle?: React.ReactNode
  mobileDescription?: React.ReactNode
  mobileFooter?: React.ReactNode
  mobileCloseLabel?: string
  id?: string
  "aria-label"?: string
  "aria-invalid"?: boolean | "true" | "false"
  "aria-describedby"?: string
}

type SingleSelectProps<TValue extends string = string> = SelectBaseProps<TValue> & {
  isMulti?: false
  value?: TValue
  defaultValue?: TValue
  onValueChange?: (nextValue: TValue) => void
}

type MultiSelectProps<TValue extends string = string> = SelectBaseProps<TValue> & {
  isMulti: true
  value?: TValue[]
  defaultValue?: TValue[]
  onValueChange?: (nextValue: TValue[]) => void
}

export type SelectProps<TValue extends string = string> = SingleSelectProps<TValue> | MultiSelectProps<TValue>

type LookupSelectBaseProps<TValue extends string = string> = Omit<SelectBaseProps<TValue>, "isSearchable">

type LookupSingleSelectProps<TValue extends string = string> = LookupSelectBaseProps<TValue> & {
  isMulti?: false
  value?: TValue
  defaultValue?: TValue
  onValueChange?: (nextValue: TValue) => void
}

type LookupMultiSelectProps<TValue extends string = string> = LookupSelectBaseProps<TValue> & {
  isMulti: true
  value?: TValue[]
  defaultValue?: TValue[]
  onValueChange?: (nextValue: TValue[]) => void
}

export type LookupSelectProps<TValue extends string = string> = (LookupSingleSelectProps<TValue> | LookupMultiSelectProps<TValue>) & {
  defaultSearchValue?: string
  isLoading?: boolean
  loadingMessage?: React.ReactNode
  onSearchValueChange?: (nextValue: string) => void
  searchPlaceholder?: string
  searchValue?: string
}

type NormalizedSelectOption<TValue extends string = string> = SelectOption<TValue> & {
  labelText: string
}

type DesktopSelectOption<TValue extends string = string> = NormalizedSelectOption<TValue>
type DesktopSelectOptions<TValue extends string = string> = Array<DesktopSelectOption<TValue> | GroupBase<DesktopSelectOption<TValue>>>

type SharedSelectImplProps<TValue extends string = string> = (SingleSelectProps<TValue> | MultiSelectProps<TValue>) & {
  controlledSearchValue?: string
  defaultSearchValue?: string
  disableClientFiltering?: boolean
  isLoading?: boolean
  loadingMessage?: React.ReactNode
  onSearchValueChange?: (nextValue: string) => void
  searchPlaceholder?: string
  searchEnabled?: boolean
}

const DEFAULT_EMPTY_MESSAGE = "No options available."
const DEFAULT_LOOKUP_LOADING = "Loading options..."

function getNodeText(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node)
  }

  if (Array.isArray(node)) {
    return node.map(getNodeText).join(" ").trim()
  }

  if (React.isValidElement(node)) {
    const props = node.props as { children?: React.ReactNode }
    return getNodeText(props.children)
  }

  return ""
}

function isSelectOptionGroup<TValue extends string = string>(option: SelectOptionEntry<TValue>): option is SelectOptionGroup<TValue> {
  return "options" in option
}

function normalizeOption<TValue extends string = string>(option: SelectOption<TValue>): NormalizedSelectOption<TValue> {
  return {
    ...option,
    labelText: option.textValue?.trim() || getNodeText(option.label).trim() || option.value,
  }
}

function normalizeOptions<TValue extends string = string>(options: SelectOptionEntry<TValue>[]) {
  const desktopOptions: DesktopSelectOptions<TValue> = []
  const allOptions: NormalizedSelectOption<TValue>[] = []

  for (const entry of options) {
    if (isSelectOptionGroup(entry)) {
      const normalizedOptions = entry.options.map(normalizeOption)

      desktopOptions.push({
        label: getNodeText(entry.label).trim() || "Group",
        options: normalizedOptions,
      })

      for (const option of normalizedOptions) {
        allOptions.push(option)
      }

      continue
    }

    const normalizedOption = normalizeOption(entry)
    desktopOptions.push(normalizedOption)
    allOptions.push(normalizedOption)
  }

  return { allOptions, desktopOptions }
}

function resolveNoOptionsMessage(
  noOptionsMessage: SelectBaseProps["noOptionsMessage"],
  inputValue: string,
  fallback: React.ReactNode,
) {
  if (typeof noOptionsMessage === "function") {
    return noOptionsMessage({ inputValue })
  }

  return noOptionsMessage ?? fallback
}

function getControlHeight(size: SelectBaseProps["size"]) {
  // Match the shared control-height scale (Input/Button: `h-control*`) so the react-select control lines up
  // with text inputs instead of rendering ~8px shorter.
  if (size === "sm") return "var(--control-h-sm)"
  if (size === "lg") return "var(--control-h-lg)"
  return "var(--control-h)"
}

function normalizeSelectValue<TValue extends string>(value: TValue | TValue[] | undefined) {
  if (Array.isArray(value)) {
    return value
  }

  if (value === undefined || value.trim().length === 0) {
    return undefined
  }

  return value
}

function selectValuesMatch<TValue extends string>(
  left: TValue | TValue[] | undefined,
  right: TValue | TValue[] | undefined,
) {
  const normalizedLeft = normalizeSelectValue(left)
  const normalizedRight = normalizeSelectValue(right)

  if (Array.isArray(normalizedLeft) || Array.isArray(normalizedRight)) {
    const leftValues = Array.isArray(normalizedLeft) ? normalizedLeft : []
    const rightValues = Array.isArray(normalizedRight) ? normalizedRight : []
    return leftValues.length === rightValues.length && leftValues.every((value, index) => value === rightValues[index])
  }

  return normalizedLeft === normalizedRight
}

function useControllableState<T>({
  controlledValue,
  defaultValue,
  onChange,
}: {
  controlledValue: T | undefined
  defaultValue: T
  onChange?: (nextValue: T) => void
}) {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue)
  const isControlled = controlledValue !== undefined
  const value = isControlled ? controlledValue : uncontrolledValue

  const setValue = React.useCallback(
    (nextValue: T) => {
      if (!isControlled) {
        setUncontrolledValue(nextValue)
      }
      onChange?.(nextValue)
    },
    [isControlled, onChange],
  )

  return [value, setValue] as const
}

function SelectOptionComponent<TValue extends string = string>(
  props: OptionProps<DesktopSelectOption<TValue>, boolean, GroupBase<DesktopSelectOption<TValue>>>,
) {
  return (
    <components.Option {...props} className={cn(props.className, props.data.className)}>
      <div data-testid={props.data.testId}>{props.data.label}</div>
    </components.Option>
  )
}

/**
 * MenuList that lets the dropdown scroll with the wheel/trackpad even inside a Radix dialog/popover.
 *
 * The menu is portaled to `document.body`, which sits OUTSIDE the dialog's scroll lock
 * (`react-remove-scroll`). That lock has a document-level `wheel` listener that `preventDefault`s any
 * wheel originating outside the locked node — and react-select relies on NATIVE scrolling for in-bounds
 * menu scrolling — so the wheel did nothing and only the scrollbar thumb worked. We stop the wheel event
 * on the scroll container so it never reaches the document lock; native scrolling then works. We don't
 * preventDefault, and we keep react-select's own (same-element) overscroll handling intact.
 */
function ScrollableMenuList<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: MenuListProps<Option, IsMulti, Group>,
) {
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const forwardedInnerRef = props.innerRef
  const setScrollRef = React.useCallback(
    (element: HTMLDivElement | null) => {
      scrollRef.current = element
      if (typeof forwardedInnerRef === "function") {
        ;(forwardedInnerRef as (instance: HTMLDivElement | null) => void)(element)
      }
    },
    [forwardedInnerRef],
  )

  React.useEffect(() => {
    const element = scrollRef.current
    if (!element) return
    const stopWheelFromReachingScrollLock = (event: WheelEvent) => event.stopPropagation()
    element.addEventListener("wheel", stopWheelFromReachingScrollLock, { passive: true })
    return () => element.removeEventListener("wheel", stopWheelFromReachingScrollLock)
  }, [])

  return (
    <components.MenuList {...props} innerRef={setScrollRef}>
      {props.children}
    </components.MenuList>
  )
}

/**
 * One Select, two surfaces. A phone opens the app's bottom sheet list (the same one every phone picker uses);
 * a larger screen keeps react-select's anchored menu.
 */
function SharedSelectImpl<TValue extends string = string>(props: SharedSelectImplProps<TValue>) {
  const isMobile = useIsMobile()
  return isMobile ? <MobileSelectImpl {...props} /> : <DesktopSelectImpl {...props} />
}

function toMobileOptions<TValue extends string>(
  options: SelectOptionEntry<TValue>[],
  renderItem: MobileSelectItemRenderer<TValue> | undefined,
  isChosen: (value: TValue) => boolean,
): MobilePickerOption[] {
  const toRow = (option: SelectOption<TValue>, group?: React.ReactNode): MobilePickerOption => {
    const normalized = normalizeOption(option)
    return {
      value: option.value,
      label: renderItem
        ? renderItem(option, { selected: isChosen(option.value), disabled: Boolean(option.disabled) })
        : option.label,
      searchText: normalized.labelText,
      disabled: option.disabled,
      group,
    }
  }
  return options.flatMap((entry) =>
    isSelectOptionGroup(entry) ? entry.options.map((option) => toRow(option, entry.label)) : [toRow(entry)],
  )
}

function MobileSelectImpl<TValue extends string = string>({
  options,
  open: openProp,
  defaultOpen,
  onOpenChange,
  value: valueProp,
  defaultValue,
  onValueChange,
  disabled = false,
  isMulti = false,
  coloredPills = false,
  placeholder,
  className,
  controlClassName,
  isClearable = false,
  clearLabel,
  creatable = false,
  formatCreateLabel,
  size = "default",
  noOptionsMessage,
  createAction,
  mobileItemRenderer,
  mobileEmpty,
  mobileTitle,
  mobileDescription,
  mobileFooter,
  mobileCloseLabel,
  id,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  controlledSearchValue,
  defaultSearchValue,
  isLoading = false,
  loadingMessage,
  onSearchValueChange,
  searchPlaceholder,
  searchEnabled = false,
}: SharedSelectImplProps<TValue>) {
  const labels = useMobilePickerLabels()
  const [open, setOpen] = useControllableState<boolean>({
    controlledValue: openProp,
    defaultValue: defaultOpen ?? false,
    onChange: onOpenChange,
  })
  const [value, setValue] = useControllableState<TValue | TValue[] | undefined>({
    controlledValue: valueProp,
    defaultValue,
    onChange: (nextValue) => {
      if (nextValue === undefined) return
      ;(onValueChange as ((nextValue: TValue | TValue[]) => void) | undefined)?.(nextValue)
    },
  })
  const [, setSearchValue] = useControllableState<string>({
    controlledValue: controlledSearchValue,
    defaultValue: defaultSearchValue ?? "",
    onChange: onSearchValueChange,
  })

  const current = normalizeSelectValue(value)
  const chosenValues: TValue[] = Array.isArray(current) ? current : current !== undefined ? [current] : []
  const { allOptions } = React.useMemo(() => normalizeOptions(options), [options])
  const labelOf = (selected: TValue) => allOptions.find((option) => option.value === selected)?.labelText ?? selected
  const mobileOptions = toMobileOptions(options, mobileItemRenderer, (candidate) => chosenValues.includes(candidate))
  const isInvalid = ariaInvalid === true || ariaInvalid === "true"
  const placeholderText = typeof placeholder === "string" ? placeholder : undefined
  const title = mobileTitle ?? ariaLabel ?? placeholderText ?? labels.search
  const emptyLabel =
    mobileEmpty ??
    (isLoading ? (loadingMessage ?? DEFAULT_LOOKUP_LOADING) : resolveNoOptionsMessage(noOptionsMessage, "", labels.noResults))

  const pick = (picked: string) => {
    const next = picked as TValue
    if (isMulti) {
      setValue(chosenValues.includes(next) ? chosenValues.filter((item) => item !== next) : [...chosenValues, next])
      return
    }
    setValue(next)
  }

  return (
    <div className={cn("w-full", className)}>
      <button
        type='button'
        id={id}
        disabled={disabled}
        aria-haspopup='dialog'
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        {...(ariaDescribedBy ? { "aria-describedby": ariaDescribedBy } : {})}
        onClick={() => setOpen(true)}
        style={{ minHeight: getControlHeight(size), height: isMulti ? "auto" : getControlHeight(size) }}
        className={cn(
          "flex w-full items-center gap-2 rounded-[var(--control-radius)] border bg-[var(--field-surface)] px-2.5 text-left text-[length:var(--control-font-size)] shadow-[0_1px_2px_0_rgba(0,0,0,0.08),0_2px_4px_-1px_rgba(0,0,0,0.1)] transition-colors",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none",
          "disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none",
          isInvalid ? "border-destructive" : "border-border",
          isMulti && "py-1",
          controlClassName,
        )}
      >
        <span className={cn("flex min-w-0 flex-1 items-center gap-1", isMulti && "flex-wrap")}>
          {chosenValues.length === 0 ? (
            <span className='text-muted-foreground truncate'>{placeholder}</span>
          ) : isMulti ? (
            chosenValues.map((selected) => (
              <span
                key={selected}
                className={cn(
                  "max-w-full truncate rounded-md px-1.5 py-0.5 text-xs font-medium",
                  coloredPills ? cn("rounded-full border", pillColorClasses(selected)) : "bg-muted",
                )}
              >
                {labelOf(selected)}
              </span>
            ))
          ) : (
            <span className='truncate text-[var(--input-foreground)]'>{labelOf(chosenValues[0] as TValue)}</span>
          )}
        </span>
        <ChevronDownIcon aria-hidden className='text-muted-foreground size-4 shrink-0' />
      </button>
      <MobilePickerSheet
        open={open}
        onOpenChange={setOpen}
        title={title}
        description={mobileDescription}
        doneLabel={mobileCloseLabel}
        options={mobileOptions}
        value={isMulti ? undefined : (chosenValues[0] ?? null)}
        values={isMulti ? chosenValues : undefined}
        onSelect={pick}
        searchPlaceholder={searchPlaceholder}
        emptyLabel={emptyLabel}
        onQueryChange={searchEnabled ? setSearchValue : undefined}
        loading={isLoading}
        clearLabel={isClearable && chosenValues.length > 0 ? (typeof clearLabel === "string" ? clearLabel : labels.clear) : undefined}
        onClear={isClearable ? () => setValue((isMulti ? [] : "") as TValue | TValue[]) : undefined}
        onCreate={
          creatable
            ? (query) => {
                const created = query.trim() as TValue
                if (!created) return
                setValue(isMulti ? Array.from(new Set([...chosenValues, created])) : created)
              }
            : undefined
        }
        formatCreateLabel={formatCreateLabel}
        actions={createAction ? [{ label: createAction.label, onSelect: createAction.onClick }] : undefined}
        footer={mobileFooter}
      />
    </div>
  )
}

function DesktopSelectImpl<TValue extends string = string>({
  options,
  open: openProp,
  defaultOpen,
  onOpenChange,
  value: valueProp,
  defaultValue,
  onValueChange,
  disabled = false,
  isMulti = false,
  isSearchable = false,
  coloredPills = false,
  hideSelectedOptions = true,
  placeholder,
  className,
  controlClassName,
  menuClassName,
  isClearable = false,
  creatable = false,
  formatCreateLabel,
  size = "default",
  menuAlign = "start",
  noOptionsMessage,
  createAction,
  mobileItemRenderer: _mobileItemRenderer,
  mobileEmpty: _mobileEmpty,
  mobileTitle: _mobileTitle,
  mobileDescription: _mobileDescription,
  mobileFooter: _mobileFooter,
  mobileCloseLabel: _mobileCloseLabel,
  id,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  controlledSearchValue,
  defaultSearchValue,
  disableClientFiltering = false,
  isLoading = false,
  loadingMessage,
  onSearchValueChange,
  searchEnabled = false,
}: SharedSelectImplProps<TValue>) {
  void _mobileItemRenderer
  void _mobileEmpty
  void _mobileTitle
  void _mobileDescription
  void _mobileFooter
  void _mobileCloseLabel

  const [open, setOpen] = useControllableState<boolean>({
    controlledValue: openProp,
    defaultValue: defaultOpen ?? false,
    onChange: onOpenChange,
  })
  const [value, setValue] = useControllableState<TValue | TValue[] | undefined>({
    controlledValue: valueProp,
    defaultValue,
    onChange: (nextValue) => {
      if (nextValue === undefined) return
      ;(onValueChange as ((nextValue: TValue | TValue[]) => void) | undefined)?.(nextValue)
    },
  })
  const [searchValue, setSearchValue] = useControllableState<string>({
    controlledValue: controlledSearchValue,
    defaultValue: defaultSearchValue ?? "",
    onChange: onSearchValueChange,
  })
  const [optimisticValue, setOptimisticValue] = React.useState<TValue | TValue[] | undefined | null>(null)
  const displayValue = optimisticValue ?? normalizeSelectValue(value)

  React.useEffect(() => {
    if (optimisticValue === null) return
    if (selectValuesMatch(optimisticValue, valueProp)) {
      setOptimisticValue(null)
    }
  }, [optimisticValue, valueProp])

  const { allOptions, desktopOptions } = React.useMemo(() => normalizeOptions(options), [options])
  const selectedOption = React.useMemo(() => {
    if (Array.isArray(displayValue)) return null
    if (displayValue === undefined) return null
    const found = allOptions.find((option) => option.value === displayValue) ?? null
    if (found) return found
    if (creatable && typeof displayValue === "string" && displayValue.length > 0) {
      return { value: displayValue, label: displayValue, labelText: displayValue } as NormalizedSelectOption<TValue>
    }
    return null
  }, [allOptions, creatable, displayValue])
  const selectedOptions = React.useMemo(() => {
    if (!Array.isArray(displayValue)) return []

    return displayValue.map((selectedValue) => {
      const found = allOptions.find((option) => option.value === selectedValue)
      if (found) return found
      if (creatable && selectedValue.length > 0) {
        return {
          value: selectedValue,
          label: selectedValue,
          labelText: selectedValue,
        } as NormalizedSelectOption<TValue>
      }
      return null
    }).filter((option): option is NormalizedSelectOption<TValue> => option !== null)
  }, [allOptions, creatable, displayValue])
  const instanceId = React.useId()
  const menuPortalTarget = typeof window !== "undefined" ? document.body : undefined
  const isInvalid = ariaInvalid === true || ariaInvalid === "true"
  const selectRef = React.useRef<SelectInstance<
    DesktopSelectOption<TValue>,
    boolean,
    GroupBase<DesktopSelectOption<TValue>>
  > | null>(null)
  // Keep the latest createAction reachable from the memoized Menu override without re-creating it each render.
  const createActionRef = React.useRef(createAction)
  createActionRef.current = createAction
  /*
   * The create row is rendered by the Menu override, OUTSIDE react-select's option list, so moving onto it
   * never fires an option's mouse handler and react-select keeps its last focused option highlighted. Two
   * rows then look active at once. Track the hover here and drop the focus highlight while it is true;
   * keyboard navigation is untouched, because a keyboard never enters this row.
   */
  const [isCreateRowHovered, setIsCreateRowHovered] = React.useState(false)
  const setCreateRowHoveredRef = React.useRef(setIsCreateRowHovered)
  setCreateRowHoveredRef.current = setIsCreateRowHovered
  const animatedComponents = React.useMemo(
    () => ({
      ...makeAnimated(),
      DropdownIndicator: (
        indicatorProps: DropdownIndicatorProps<DesktopSelectOption<TValue>, boolean, GroupBase<DesktopSelectOption<TValue>>>,
      ) => (
        <components.DropdownIndicator {...indicatorProps}>
          <ChevronDownIcon className='size-4' />
        </components.DropdownIndicator>
      ),
      ClearIndicator: (
        clearIndicatorProps: ClearIndicatorProps<DesktopSelectOption<TValue>, boolean, GroupBase<DesktopSelectOption<TValue>>>,
      ) => (
        <components.ClearIndicator {...clearIndicatorProps}>
          <XIcon className='size-3.5' />
        </components.ClearIndicator>
      ),
      GroupHeading: (
        groupProps: GroupHeadingProps<DesktopSelectOption<TValue>, boolean, GroupBase<DesktopSelectOption<TValue>>>,
      ) => (
        <components.GroupHeading {...groupProps}>{groupProps.data.label}</components.GroupHeading>
      ),
      Option: SelectOptionComponent,
      MenuList: ScrollableMenuList,
      // Sticky "create" row below the scrollable list (outside MenuList so it doesn't scroll away).
      Menu: (menuProps: MenuProps<DesktopSelectOption<TValue>, boolean, GroupBase<DesktopSelectOption<TValue>>>) => {
        const action = createActionRef.current
        return (
          <components.Menu {...menuProps}>
            {menuProps.children}
            {action ? (
              <button
                type='button'
                // Keep focus on the select input so the explicit blur() below closes the menu predictably.
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setCreateRowHoveredRef.current(true)}
                onMouseLeave={() => setCreateRowHoveredRef.current(false)}
                onClick={() => {
                  setCreateRowHoveredRef.current(false)
                  selectRef.current?.blur()
                  action.onClick()
                }}
                className='text-primary hover:bg-primary/8 flex w-full items-center gap-2 border-t border-border px-3 py-2.5 text-left text-sm font-medium transition-colors'
              >
                <PlusIcon className='size-4 shrink-0' />
                <span className='min-w-0 truncate'>{action.label}</span>
              </button>
            ) : null}
          </components.Menu>
        )
      },
      MultiValueLabel: (
        multiValueLabelProps: MultiValueGenericProps<DesktopSelectOption<TValue>, boolean, GroupBase<DesktopSelectOption<TValue>>>,
      ) => (
        <components.MultiValueLabel {...multiValueLabelProps}>
          {multiValueLabelProps.data.labelText}
        </components.MultiValueLabel>
      ),
    }),
    [],
  )

  const controlHeight = getControlHeight(size)
  const resolvedLoadingMessage = loadingMessage ?? DEFAULT_LOOKUP_LOADING

  const handleSelectValueChange = React.useCallback(
    (nextValue: TValue | TValue[]) => {
      setOptimisticValue(nextValue)
      setValue(nextValue)
      if (searchEnabled || creatable) {
        setSearchValue("")
      }
      if (!Array.isArray(nextValue)) {
        setOpen(false)
      }
    },
    [creatable, searchEnabled, setOpen, setSearchValue, setValue],
  )

  const handleDesktopInputChange = React.useCallback(
    (nextValue: string, meta: InputActionMeta) => {
      if (!searchEnabled) return

      switch (meta.action) {
        case "input-change":
          setSearchValue(nextValue)
          return
        case "menu-close":
        case "input-blur":
          setSearchValue("")
          return
        default:
          return
      }
    },
    [searchEnabled, setSearchValue],
  )

  // Creatable selects use react-select's CreatableSelect. Its props are a superset of ReactSelect's,
  // so the cast keeps the JSX below unchanged and the create-* props are only passed when `creatable` is set.
  const SelectComponent = (creatable ? CreatableSelect : ReactSelect) as typeof CreatableSelect
  const resolvedCreateLabel = React.useCallback(
    (input: string) => formatCreateLabel?.(input) ?? input,
    [formatCreateLabel],
  )
  const currentMultiValues = Array.isArray(displayValue) ? displayValue : []

  return (
    <div className={cn("w-full", className)}>
      <SelectComponent<DesktopSelectOption<TValue>, boolean, GroupBase<DesktopSelectOption<TValue>>>
        ref={selectRef}
        onCreateOption={
          creatable
            ? (input: string) => {
                const trimmedInput = input.trim() as TValue
                if (!trimmedInput) return
                if (isMulti) {
                  const nextValues = Array.from(new Set([...currentMultiValues, trimmedInput]))
                  handleSelectValueChange(nextValues)
                  return
                }

                handleSelectValueChange(trimmedInput)
              }
            : undefined
        }
        formatCreateLabel={creatable ? (input: string) => resolvedCreateLabel(input) : undefined}
        createOptionPosition={creatable ? "first" : undefined}
        isValidNewOption={
          creatable
            ? (input: string) => {
                const trimmedInput = input.trim().toLowerCase()
                if (!trimmedInput) return false
                if (isMulti && currentMultiValues.some((currentValue) => currentValue.toLowerCase() === trimmedInput)) {
                  return false
                }
                return true
              }
            : undefined
        }
        getNewOptionData={
          creatable
            ? (inputValue: string, optionLabel: React.ReactNode) =>
                ({
                  value: inputValue as TValue,
                  label: typeof optionLabel === "string" ? optionLabel : inputValue,
                  labelText: inputValue,
                }) as DesktopSelectOption<TValue>
            : undefined
        }
        instanceId={id ?? instanceId}
        inputId={id}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        isDisabled={disabled}
        isOptionDisabled={(option) => Boolean((option as NormalizedSelectOption<TValue>).disabled)}
        isMulti={isMulti}
        isClearable={isClearable && !disabled}
        isSearchable={searchEnabled || creatable ? true : isSearchable}
        isLoading={isLoading}
        openMenuOnFocus
        closeMenuOnSelect={!isMulti}
        hideSelectedOptions={hideSelectedOptions}
        value={isMulti ? selectedOptions : selectedOption}
        options={desktopOptions}
        placeholder={placeholder}
        onInputChange={handleDesktopInputChange}
        filterOption={disableClientFiltering ? () => true : undefined}
        noOptionsMessage={({ inputValue }) =>
          resolveNoOptionsMessage(noOptionsMessage, inputValue, isLoading ? resolvedLoadingMessage : DEFAULT_EMPTY_MESSAGE)
        }
        loadingMessage={() => resolvedLoadingMessage}
        menuPlacement='auto'
        menuPosition='fixed'
        menuPortalTarget={menuPortalTarget}
        components={animatedComponents}
        classNamePrefix='ibada-select'
        blurInputOnSelect
        classNames={{
          control: () => cn(controlClassName),
          menu: () => cn(menuClassName),
          ...(coloredPills
            ? {
                multiValue: (multiValueProps) => cn("!rounded-full !border", pillColorClasses(multiValueProps.data.value)),
                multiValueRemove: () => "!rounded-full",
              }
            : {}),
        }}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: "var(--primary)",
            primary25: "color-mix(in srgb, var(--primary) 10%, var(--popover))",
            primary50: "color-mix(in srgb, var(--primary) 18%, var(--popover))",
            neutral0: "var(--input)",
            neutral5: "var(--popover)",
            neutral10: "var(--border)",
            neutral20: "var(--border)",
            neutral30: "var(--ring)",
            neutral40: "var(--muted-foreground)",
            neutral50: "var(--muted-foreground)",
            neutral60: "var(--foreground)",
            neutral70: "var(--foreground)",
            neutral80: "var(--input-foreground)",
            neutral90: "var(--foreground)",
            danger: "var(--destructive)",
            dangerLight: "color-mix(in srgb, var(--destructive) 10%, var(--popover))",
          },
        })}
        styles={{
          control: (base, state) => ({
            ...base,
            minHeight: controlHeight,
            // Multi-select grows to fit wrapped chips; single-select stays a fixed control height.
            height: isMulti ? "auto" : controlHeight,
            backgroundColor: "var(--field-surface)",
            fontSize: "var(--control-font-size)",
            borderColor: state.isDisabled
              ? "color-mix(in srgb, var(--border) 88%, var(--muted-foreground) 12%)"
              : isInvalid
                ? "var(--destructive)"
                : state.isFocused
                  ? "var(--ring)"
                  : "var(--border)",
            borderRadius: "var(--control-radius)",
            boxShadow:
              state.isDisabled
                ? "none"
                : state.isFocused
                  ? "0 0 0 3px color-mix(in srgb, var(--ring) 50%, transparent)"
                  : "0 1px 2px 0 rgba(0,0,0,0.08), 0 2px 4px -1px rgba(0,0,0,0.1)",
            opacity: state.isDisabled ? 0.6 : 1,
            cursor: state.isDisabled ? "not-allowed" : "default",
            "&:hover": {
              borderColor: state.isDisabled
                ? "color-mix(in srgb, var(--border) 88%, var(--muted-foreground) 12%)"
                : isInvalid
                  ? "var(--destructive)"
                  : state.isFocused
                    ? "var(--ring)"
                    : "var(--border)",
            },
          }),
          valueContainer: (base) => ({
            ...base,
            // Multi-select: let chips wrap onto new lines (auto height) instead of being clipped to one row.
            height: isMulti ? "auto" : "100%",
            flexWrap: isMulti ? "wrap" : base.flexWrap,
            padding: isMulti ? "3px 10px" : "0 10px",
          }),
          placeholder: (base) => ({
            ...base,
            color: disabled
              ? "color-mix(in srgb, var(--muted-foreground) 88%, transparent)"
              : "var(--muted-foreground)",
            fontSize: 14,
            // react-select truncates `singleValue` but NOT the placeholder, so a long placeholder wrapped onto a
            // second line inside a fixed-height control and got sliced in half. A placeholder is a hint, not
            // content: one line, ellipsised. (`minWidth: 0` — it is a grid item, which otherwise refuses to
            // shrink below its text and never overflows in the first place.)
            minWidth: 0,
            maxWidth: "100%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }),
          singleValue: (base) => ({
            ...base,
            color: disabled
              ? "color-mix(in srgb, var(--foreground) 72%, transparent)"
              : "var(--input-foreground)",
            fontSize: 14,
            margin: 0,
          }),
          input: (base) => ({
            ...base,
            color: disabled
              ? "color-mix(in srgb, var(--input-foreground) 68%, transparent)"
              : "var(--input-foreground)",
            fontSize: 14,
            margin: 0,
            padding: 0,
          }),
          indicatorsContainer: (base) => ({
            ...base,
            // Single-select fills the fixed control height. Multi grows, so let the indicators stretch to the
            // control's (auto) height and center the chevron vertically — a fixed 100% height clipped it.
            height: isMulti ? "auto" : "100%",
            alignSelf: "stretch",
            opacity: disabled ? 0.45 : 1,
          }),
          clearIndicator: (base) => ({
            ...base,
            padding: 4,
            display: disabled ? "none" : base.display,
          }),
          dropdownIndicator: (base) => ({
            ...base,
            padding: 4,
            color: disabled
              ? "color-mix(in srgb, var(--muted-foreground) 82%, transparent)"
              : base.color,
          }),
          indicatorSeparator: (base) => ({
            ...base,
            marginBottom: 6,
            marginTop: 6,
            opacity: disabled ? 0.5 : 1,
          }),
          option: (base, state) => ({
            ...base,
            minHeight: 36,
            display: "flex",
            alignItems: "center",
            padding: "8px 10px",
            fontSize: 14,
            lineHeight: 1.35,
            color: state.isDisabled
              ? "var(--muted-foreground)"
              : state.isSelected
                ? "var(--primary)"
                : "var(--popover-foreground)",
            backgroundColor: state.isDisabled
              ? "transparent"
              : state.isSelected || (state.isFocused && !isCreateRowHovered)
                ? "color-mix(in srgb, var(--primary) 10%, var(--popover))"
                : "transparent",
            cursor: state.isDisabled ? "not-allowed" : "default",
            borderRadius: "calc(var(--radius) - 1px)",
            ":active": {
              backgroundColor: state.isDisabled
                ? "transparent"
                : "color-mix(in srgb, var(--primary) 14%, var(--popover))",
            },
            // Disabled options stay clearly legible (react-select's default fades them to near-invisible):
            // keep a readable muted tone rather than a washed-out opacity.
            ...(state.isDisabled
              ? { opacity: 1 }
              : {}),
          }),
          multiValue: (base) =>
            coloredPills ? { ...base, backgroundColor: undefined, borderRadius: undefined, paddingLeft: 2 } : base,
          multiValueLabel: (base) => ({
            ...base,
            fontSize: 12,
            fontWeight: 500,
            ...(coloredPills ? { color: undefined } : {}),
          }),
          multiValueRemove: (base) =>
            coloredPills
              ? { ...base, color: undefined, ":hover": { backgroundColor: "rgba(0,0,0,0.18)", color: undefined } }
              : base,
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
            pointerEvents: "auto",
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999,
            pointerEvents: "auto",
            marginTop: 4,
            marginBottom: 4,
            padding: 4,
            overflow: "hidden",
            backgroundColor: "var(--popover)",
            color: "var(--popover-foreground)",
            border: "0",
            borderRadius: "var(--surface-radius)",
            boxShadow:
              "var(--shadow-overlay), inset 0 0 0 1px color-mix(in srgb, var(--border) 48%, transparent)",
            ...(menuAlign === "end" ? { left: "auto", right: 0 } : {}),
          }),
          menuList: (base) => ({
            ...base,
            padding: 0,
          }),
          noOptionsMessage: (base) => ({
            ...base,
            color: "var(--muted-foreground)",
            padding: "12px 10px",
          }),
          loadingMessage: (base) => ({
            ...base,
            color: "var(--muted-foreground)",
            padding: "12px 10px",
          }),
        }}
        getOptionValue={(option) => option.value}
        getOptionLabel={(option) => option.labelText}
        onMenuOpen={() => setOpen(true)}
        onMenuClose={() => setOpen(false)}
        onChange={(nextOption) => {
          if (Array.isArray(nextOption)) {
            handleSelectValueChange(nextOption.map((option) => option.value))
            return
          }

          handleSelectValueChange((nextOption as DesktopSelectOption<TValue> | null)?.value ?? ("" as TValue))
        }}
        {...(ariaDescribedBy ? { "aria-describedby": ariaDescribedBy } : {})}
      />
    </div>
  )
}

function Select<TValue extends string = string>(props: SelectProps<TValue>) {
  return <SharedSelectImpl {...props} searchEnabled={false} />
}

function LookupSelect<TValue extends string = string>({
  searchValue,
  defaultSearchValue,
  onSearchValueChange,
  searchPlaceholder,
  isLoading = false,
  loadingMessage,
  ...props
}: LookupSelectProps<TValue>) {
  return (
    <SharedSelectImpl
      {...props}
      controlledSearchValue={searchValue}
      defaultSearchValue={defaultSearchValue}
      onSearchValueChange={onSearchValueChange}
      searchPlaceholder={searchPlaceholder}
      searchEnabled
      disableClientFiltering
      isLoading={isLoading}
      loadingMessage={loadingMessage}
    />
  )
}

export { Select, LookupSelect }
